'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

const USER_GROWTH = [
  { date: '10 mai', users: 4211, new: 76, active: 1876 },
  { date: '11 mai', users: 4287, new: 103, active: 2012 },
  { date: '12 mai', users: 4421, new: 134, active: 2234 },
  { date: '13 mai', users: 4510, new: 89, active: 1987 },
  { date: '14 mai', users: 4622, new: 112, active: 2087 },
  { date: '15 mai', users: 4720, new: 98, active: 2198 },
  { date: '16 mai', users: 4847, new: 127, active: 2341 },
];

const ENGAGEMENT_DATA = [
  { page: 'Dashboard', views: 18934, avg_time: '4m 23s' },
  { page: 'Live Analytics', views: 12456, avg_time: '8m 12s' },
  { page: 'Projections', views: 9876, avg_time: '6m 45s' },
  { page: 'ML Engine', views: 7234, avg_time: '5m 18s' },
  { page: 'Alertes', views: 6789, avg_time: '3m 02s' },
  { page: 'AI Insights', views: 5432, avg_time: '7m 34s' },
  { page: 'Backtesting', views: 4321, avg_time: '9m 56s' },
];

const CONVERSION_FUNNEL = [
  { stage: 'Visiteurs', count: 24567, pct: 100 },
  { stage: 'Inscriptions', count: 4847, pct: 19.7 },
  { stage: 'Actifs 7j', count: 2341, pct: 48.3 },
  { stage: 'Trial', count: 567, pct: 11.7 },
  { stage: 'Payants', count: 955, pct: 19.7 },
];

const RETENTION_DATA = [
  { cohort: 'Jan 2026', w1: 78, w2: 64, w4: 52, w8: 41, w12: 35 },
  { cohort: 'Fév 2026', w1: 81, w2: 67, w4: 55, w8: 44, w12: 38 },
  { cohort: 'Mar 2026', w1: 79, w2: 65, w4: 53, w8: 43, w12: null },
  { cohort: 'Avr 2026', w1: 83, w2: 69, w4: 57, w8: null, w12: null },
  { cohort: 'Mai 2026', w1: 85, w2: 71, w4: null, w8: null, w12: null },
];

const PLAN_PIE = [
  { name: 'FREE', value: 3892, color: '#6B7A99' },
  { name: 'Pro $29', value: 955, color: '#00D4FF' },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

export default function BusinessIntelligenceClient() {
  const [activeTab, setActiveTab] = useState<'growth' | 'engagement' | 'retention' | 'reports'>('growth');

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Business Intelligence</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Dashboard admin · Croissance · Engagement · Rétention · Rapports automatiques</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-ghost flex items-center gap-2 text-sm">
            <Icon name="DocumentArrowDownIcon" size={16} className="text-muted-foreground" />
            Exporter Rapport
          </button>
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
            {(['growth', 'engagement', 'retention', 'reports'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === tab ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tab === 'growth' ? 'Croissance' : tab === 'engagement' ? 'Engagement' : tab === 'retention' ? 'Rétention' : 'Rapports'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Utilisateurs Total', value: '4,847', delta: '+127 aujourd\'hui', color: 'text-primary', icon: 'UsersIcon' },
          { label: 'Utilisateurs Actifs', value: '2,341', delta: '48.3% du total', color: 'text-positive', icon: 'UserCircleIcon' },
          { label: 'Abonnés Payants', value: '955', delta: '19.7% conversion', color: 'text-warning', icon: 'CreditCardIcon' },
          { label: 'Vues Projections', value: '18,934', delta: 'Aujourd\'hui', color: 'text-foreground', icon: 'ChartBarIcon' },
        ].map((kpi) => (
          <div key={kpi.label} className="card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={14} className={kpi.color} />
              <span className="text-xs text-muted-foreground">{kpi.label}</span>
            </div>
            <div className={`text-2xl font-bold font-mono-data ${kpi.color}`}>{kpi.value}</div>
            <div className="text-2xs text-muted-foreground mt-1">{kpi.delta}</div>
          </div>
        ))}
      </div>

      {activeTab === 'growth' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Croissance Utilisateurs (7 jours)</h3>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={USER_GROWTH}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                  <XAxis dataKey="date" tick={{ fill: '#6B7A99', fontSize: 9 }} />
                  <YAxis tick={{ fill: '#6B7A99', fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Area type="monotone" dataKey="users" stroke="#00D4FF" fill="#00D4FF" fillOpacity={0.1} strokeWidth={2} name="Total" />
                  <Area type="monotone" dataKey="active" stroke="#00C853" fill="#00C853" fillOpacity={0.1} strokeWidth={1.5} name="Actifs" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="card p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Distribution Plans</h3>
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie data={PLAN_PIE} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value">
                      {PLAN_PIE.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2">
                  {PLAN_PIE.map((p) => (
                    <div key={p.name} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: p.color }} />
                      <span className="text-xs text-muted-foreground">{p.name}</span>
                      <span className="text-xs font-mono-data text-foreground ml-auto">{p.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Entonnoir de Conversion</h3>
            <div className="space-y-2">
              {CONVERSION_FUNNEL.map((stage, i) => (
                <div key={stage.stage} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-24">{stage.stage}</span>
                  <div className="flex-1 h-6 bg-card-hover rounded overflow-hidden">
                    <div
                      className="h-full flex items-center px-2 rounded transition-all"
                      style={{ width: `${stage.pct}%`, backgroundColor: `hsl(${200 - i * 30}, 70%, 50%)`, opacity: 0.8 }}
                    >
                      <span className="text-2xs font-mono-data text-white">{stage.count.toLocaleString()}</span>
                    </div>
                  </div>
                  <span className="text-xs font-mono-data text-muted-foreground w-12 text-right">{stage.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'engagement' && (
        <>
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Pages les Plus Visitées</h3>
            <div className="space-y-3">
              {ENGAGEMENT_DATA.map((page, i) => (
                <div key={page.page} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-6 text-right">{i + 1}</span>
                  <span className="text-xs text-foreground w-32">{page.page}</span>
                  <div className="flex-1 h-2 bg-card-hover rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(page.views / 18934) * 100}%` }} />
                  </div>
                  <span className="text-xs font-mono-data text-foreground w-16 text-right">{page.views.toLocaleString()}</span>
                  <span className="text-2xs text-muted-foreground w-16 text-right">{page.avg_time}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Activité Live Aujourd&apos;hui</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Sessions Live Analytics', value: '4,521', icon: 'BoltIcon', color: 'text-positive' },
                { label: 'Projections Consultées', value: '18,934', icon: 'ChartBarIcon', color: 'text-primary' },
                { label: 'Alertes Déclenchées', value: '234', icon: 'BellAlertIcon', color: 'text-warning' },
                { label: 'Appels API', value: '125,678', icon: 'ServerIcon', color: 'text-foreground' },
              ].map((stat) => (
                <div key={stat.label} className="p-3 rounded-lg bg-card-hover text-center">
                  <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className={`${stat.color} mx-auto mb-2`} />
                  <div className={`text-xl font-bold font-mono-data ${stat.color}`}>{stat.value}</div>
                  <div className="text-2xs text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'retention' && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Heatmap de Rétention par Cohorte</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-muted-foreground">Cohorte</th>
                  {['Sem 1', 'Sem 2', 'Sem 4', 'Sem 8', 'Sem 12'].map((h) => (
                    <th key={h} className="text-center py-2 px-3 text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RETENTION_DATA.map((row) => (
                  <tr key={row.cohort} className="border-b border-border/50">
                    <td className="py-2 px-3 text-foreground font-medium">{row.cohort}</td>
                    {[row.w1, row.w2, row.w4, row.w8, row.w12].map((val, i) => (
                      <td key={i} className="py-2 px-3 text-center">
                        {val !== null ? (
                          <span
                            className="inline-block px-2 py-1 rounded text-xs font-mono-data font-semibold"
                            style={{
                              backgroundColor: `rgba(0, 212, 255, ${val / 100})`,
                              color: val > 50 ? '#fff' : '#E8EDF5',
                            }}
                          >
                            {val}%
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="DocumentTextIcon" size={16} className="text-primary" />
              Rapports Automatiques
            </h3>
            <div className="space-y-3">
              {[
                { name: 'Rapport Hebdomadaire', schedule: 'Chaque lundi 08:00', last: 'Lun 13 mai', recipients: 3, status: 'active' },
                { name: 'Rapport MRR Mensuel', schedule: '1er du mois 09:00', last: '1 mai 2026', recipients: 5, status: 'active' },
                { name: 'Rapport Churn', schedule: 'Chaque vendredi 17:00', last: 'Ven 10 mai', recipients: 2, status: 'active' },
                { name: 'Rapport Sécurité', schedule: 'Quotidien 06:00', last: 'Aujourd\'hui', recipients: 1, status: 'active' },
              ].map((report) => (
                <div key={report.name} className="p-3 rounded-lg bg-card-hover border border-border/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-foreground">{report.name}</span>
                    <span className="status-badge bg-positive-subtle text-positive text-2xs">Actif</span>
                  </div>
                  <div className="text-2xs text-muted-foreground">{report.schedule}</div>
                  <div className="flex gap-4 mt-2 text-2xs">
                    <span className="text-muted-foreground">Dernier: <span className="text-foreground">{report.last}</span></span>
                    <span className="text-muted-foreground">Destinataires: <span className="text-foreground">{report.recipients}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="BellAlertIcon" size={16} className="text-warning" />
              Alertes Admin
            </h3>
            <div className="space-y-3">
              {[
                { title: 'Churn Rate élevé', detail: 'Churn 3.1% ce vendredi — seuil 3%', severity: 'warning', time: '2j ago' },
                { title: 'Pic de trafic', detail: '+340% trafic API à 20h — playoffs', severity: 'info', time: '3j ago' },
                { title: 'Conversion record', detail: '19.1% conversion free→payant — record', severity: 'success', time: '4j ago' },
                { title: 'Nouveau marché', detail: '127 inscriptions depuis le Canada en 24h', severity: 'info', time: '5j ago' },
              ].map((alert, i) => (
                <div key={i} className={`p-3 rounded-lg border ${alert.severity === 'warning' ? 'border-warning/30 bg-warning/5' : alert.severity === 'success' ? 'border-positive/30 bg-positive/5' : 'border-border bg-card-hover'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-foreground">{alert.title}</span>
                    <span className="text-2xs text-muted-foreground">{alert.time}</span>
                  </div>
                  <p className="text-2xs text-muted-foreground">{alert.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
